import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { money, STATUS_META } from "@/lib/format";
import { Plus, FolderKanban, Loader2, X, Building2, Gauge, ShieldAlert, Trash2, CheckCircle2 } from "lucide-react";
import { LocationPicker } from "@/components/LocationPicker";

const TYPES = ["commercial", "residential", "industrial", "remodel", "infrastructure"];
const STATUSES = ["draft", "tender", "review", "approved"];

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <span className={`text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${m.cls}`}>{m.label}</span>;
}

export default function Projects() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", client: "", type: "commercial", status: "draft", description: "", location: "", client_email: "" });

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get("/projects")).data,
  });

  const createMut = useMutation({
    mutationFn: async (body) => (await api.post("/projects", body)).data,
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project created");
      setOpen(false);
      navigate(`/projects/${p.id}`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/projects/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["projects"] }); toast.success("Project deleted"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-sm text-muted-foreground mt-1">Every active bid and estimate in one place.</p>
        </div>
        <button data-testid="new-project-btn" onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 font-bold hover:bg-orange-600 active:bg-orange-700 transition-colors">
          <Plus className="h-4 w-4" /> New Project
        </button>
      </div>

      {isLoading ? (
        <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : projects.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-20 text-center">
          <FolderKanban className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-semibold">No projects yet</p>
          <p className="text-sm text-muted-foreground mb-4">Create your first project to start estimating.</p>
          <button onClick={() => setOpen(true)} className="rounded-lg bg-primary text-primary-foreground px-4 py-2 font-bold hover:bg-orange-600 transition-colors">Create project</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
          {projects.map((p) => (
            <div key={p.id} data-testid={`project-card-${p.id}`}
              className="group rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors cursor-pointer relative"
              onClick={() => navigate(`/projects/${p.id}`)}>
              <button data-testid={`delete-project-${p.id}`}
                onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${p.name}"?`)) delMut.mutate(p.id); }}
                className="absolute top-4 right-4 h-8 w-8 grid place-items-center rounded-lg text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-destructive/15 hover:text-destructive transition">
                <Trash2 className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-9 w-9 rounded-lg bg-primary/15 text-primary grid place-items-center"><Building2 className="h-4.5 w-4.5" /></div>
                <StatusBadge status={p.status} />
                {p.stats?.local_rates_applied && (
                  <span data-testid={`local-rates-badge-${p.id}`} title="Priced with real local contractor & supplier rates"
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-500 px-2 py-0.5 text-[10px] font-semibold">
                    <CheckCircle2 className="h-3 w-3" /> Local rates
                  </span>
                )}
              </div>
              <h3 className="font-semibold text-lg leading-tight pr-8 truncate">{p.name}</h3>
              <p className="text-sm text-muted-foreground mb-4 truncate">{p.client || "—"} · <span className="capitalize">{p.type}</span></p>
              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Total</div>
                  <div className="font-mono font-bold text-primary tabular-nums text-sm truncate">{money(p.stats?.boq_total, 0)}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Gauge className="h-3 w-3" />Quality</div>
                  <div className="font-mono font-bold tabular-nums text-sm">{p.stats?.quality_score ?? "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1"><ShieldAlert className="h-3 w-3" />Risk</div>
                  <div className="font-mono font-bold tabular-nums text-sm truncate">{money(p.stats?.risk_exposure, 0)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-xl border border-border bg-popover p-6 animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="new-project-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">New Project</h2>
              <button onClick={() => setOpen(false)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-secondary"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); createMut.mutate(form); }} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5">Project name</label>
                <input data-testid="project-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" placeholder="Downtown Office Fit-out" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Client</label>
                <input data-testid="project-client-input" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })}
                  className="w-full rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" placeholder="Acme Corp" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Client email <span className="font-normal text-muted-foreground">— for change-order notifications</span></label>
                <input data-testid="project-client-email-input" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                  className="w-full rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" placeholder="ops@acme.com" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Location <span className="font-normal text-muted-foreground">— city/state for local rates</span></label>
                <LocationPicker testid="project-location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} placeholder="Austin, TX" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Type</label>
                  <select data-testid="project-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary capitalize">
                    {TYPES.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Status</label>
                  <select data-testid="project-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary capitalize">
                    {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{STATUS_META[s].label}</option>)}
                  </select>
                </div>
              </div>
              <button data-testid="create-project-submit" disabled={createMut.isPending}
                className="w-full rounded-lg bg-primary text-primary-foreground py-2.5 font-bold hover:bg-orange-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {createMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Create project
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
